import React from 'react'
import { Box, Typography, Container } from '@mui/material'

export default function Terms() {
	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'flex-start',
				py: { xs: 4, md: 8 },
				px: 2
			}}
		>
			<Container maxWidth="md">
				<Box sx={{ maxWidth: 800, mx: 'auto' }}>
					<Typography
						variant="h1"
						component="h1"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 700,
							fontSize: { xs: '28px', md: '40px' },
							color: '#2D3748',
							mb: 2
						}}
					>
						Nami Terms of Service
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 500,
							fontSize: { xs: '14px', md: '16px' },
							color: '#718096',
							mb: 4
						}}
					>
						Last Updated: January 11th, 2026
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 3
						}}
					>
						These Terms of Use ("Terms") govern your access to and use of Nami's software, websites, and related services (the "Services"). In these Terms, "Nami," "we," "us," and "our" refer to Nami, and "you" refers to the person or entity using the Services.
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 4
						}}
					>
						By creating an account, clicking "Sign Up" (or similar), or otherwise using the Services, you agree to these Terms. If you do not agree, do not use the Services.
					</Typography>

					{/* Section 1 */}
					<Typography
						variant="h2"
						component="h2"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '18px', md: '22px' },
							color: '#2D3748',
							mb: 2,
							mt: 4
						}}
					>
						1) The Services and Nami's Role
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 2
						}}
					>
						Nami provides a software platform for pet care businesses and their authorized staff (each, a "Professional") to manage customer and pet information, staff, services, appointments, automated reminders, and online booking. Pet owners (each, a "Customer") may interact with booking pages and communications enabled by a Professional. Professionals and Customers are "Users."
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 3
						}}
					>
						Nami does not provide pet care services. Pet care services are provided by Professionals, and any booking or service arrangement is between the Customer and the Professional.
					</Typography>

					{/* Section 2 */}
					<Typography
						variant="h2"
						component="h2"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '18px', md: '22px' },
							color: '#2D3748',
							mb: 2,
							mt: 4
						}}
					>
						2) Accounts
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 3
						}}
					>
						Certain features require an account ("Account"). You agree to provide accurate information and keep it up to date. You are responsible for maintaining the confidentiality of your login credentials and for activity that occurs under your Account. If you believe your Account has been accessed without authorization, notify us promptly.
					</Typography>

					{/* Section 3 */}
					<Typography
						variant="h2"
						component="h2"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '18px', md: '22px' },
							color: '#2D3748',
							mb: 2,
							mt: 4
						}}
					>
						3) Acceptable Use
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 2
						}}
					>
						You agree to use the Services lawfully and not to:
					</Typography>

					<Box component="ul" sx={{ pl: 3, mb: 3, listStyleType: 'disc' }}>
						<Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: { xs: '14px', md: '16px' }, lineHeight: 1.7, color: '#4A5568', mb: 1, display: 'list-item' }}>
							make the Services available to others outside what the Services allow (including by selling, reselling, sublicensing, leasing, transferring, assigning, distributing, or time-sharing);
						</Typography>
						<Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: { xs: '14px', md: '16px' }, lineHeight: 1.7, color: '#4A5568', mb: 1, display: 'list-item' }}>
							reverse engineer, decompile, disassemble, or create derivative works from the Services or related software (except where applicable law does not allow this restriction);
						</Typography>
						<Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: { xs: '14px', md: '16px' }, lineHeight: 1.7, color: '#4A5568', mb: 1, display: 'list-item' }}>
							bypass security or access restrictions;
						</Typography>
						<Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: { xs: '14px', md: '16px' }, lineHeight: 1.7, color: '#4A5568', mb: 1, display: 'list-item' }}>
							use the Services to build a competing product or copy ideas, features, functions, or graphics;
						</Typography>
						<Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: { xs: '14px', md: '16px' }, lineHeight: 1.7, color: '#4A5568', mb: 1, display: 'list-item' }}>
							interfere with networks or systems, distribute harmful code, scrape/data-mine, or send unlawful unsolicited commercial communications; or
						</Typography>
						<Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: { xs: '14px', md: '16px' }, lineHeight: 1.7, color: '#4A5568', mb: 1, display: 'list-item' }}>
							store personal information from individuals who have not consented to your processing of their personal information.
						</Typography>
					</Box>

					{/* Section 4 */}
					<Typography
						variant="h2"
						component="h2"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '18px', md: '22px' },
							color: '#2D3748',
							mb: 2,
							mt: 4
						}}
					>
						4) Intellectual Property
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 3
						}}
					>
						Nami (and its licensors) owns the Services and related materials, including all intellectual property rights, except for the limited rights granted to you under these Terms.
					</Typography>

					{/* Section 5 */}
					<Typography
						variant="h2"
						component="h2"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '18px', md: '22px' },
							color: '#2D3748',
							mb: 2,
							mt: 4
						}}
					>
						5) User Content
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 2
						}}
					>
						"User Content" means content you submit or make available through the Services. You retain ownership of your User Content. You grant Nami a non-exclusive, worldwide, royalty-free, sublicensable license to use your User Content as needed to operate and provide the Services.
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 3
						}}
					>
						You are responsible for your User Content. Nami may remove User Content that violates these Terms or is otherwise objectionable.
					</Typography>

					{/* Section 6 */}
					<Typography
						variant="h2"
						component="h2"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '18px', md: '22px' },
							color: '#2D3748',
							mb: 2,
							mt: 4
						}}
					>
						6) Privacy
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 3
						}}
					>
						Our handling of personal information is described in the Nami Privacy Policy.
					</Typography>

					{/* Section 7 */}
					<Typography
						variant="h2"
						component="h2"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '18px', md: '22px' },
							color: '#2D3748',
							mb: 2,
							mt: 4
						}}
					>
						7) Third-Party Services
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 3
						}}
					>
						The Services may rely on third-party software, platforms, or services ("Third-Party Technology"). Your use of Third-Party Technology may be subject to separate third-party terms and privacy practices.
					</Typography>

					{/* Section 8 */}
					<Typography
						variant="h2"
						component="h2"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '18px', md: '22px' },
							color: '#2D3748',
							mb: 2,
							mt: 4
						}}
					>
						8) Changes; Suspension; Termination
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 2
						}}
					>
						We may update these Terms or change the Services by posting updated Terms or otherwise providing notice. If you continue using the Services after changes take effect, you accept the updated Terms.
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 3
						}}
					>
						We may suspend or terminate your access to the Services if you violate these Terms. If access is suspended or terminated, you must stop using the Services.
					</Typography>

					{/* Section 9 */}
					<Typography
						variant="h2"
						component="h2"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '18px', md: '22px' },
							color: '#2D3748',
							mb: 2,
							mt: 4
						}}
					>
						9) Text Messaging Terms (SMS/MMS)
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 3
						}}
					>
						This section applies when a Professional uses the Services to send or receive SMS or MMS text messages ("Text Messages") to or from a Customer ("Texting Services"). Texting Services may be provided through Twilio Inc. or its affiliates ("Twilio").
					</Typography>

					<Typography
						variant="h3"
						component="h3"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '16px', md: '18px' },
							color: '#2D3748',
							mb: 1.5,
							mt: 3
						}}
					>
						9.1 For Professionals
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 2
						}}
					>
						Professionals are responsible for:
					</Typography>

					<Box component="ul" sx={{ pl: 3, mb: 2, listStyleType: 'disc' }}>
						<Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: { xs: '14px', md: '16px' }, lineHeight: 1.7, color: '#4A5568', mb: 1, display: 'list-item' }}>
							complying with applicable laws and regulations for Text Messages, and any applicable Twilio policies/terms; and
						</Typography>
						<Typography component="li" sx={{ fontFamily: 'Inter, sans-serif', fontSize: { xs: '14px', md: '16px' }, lineHeight: 1.7, color: '#4A5568', mb: 1, display: 'list-item' }}>
							obtaining any required consent from recipients before sending Text Messages.
						</Typography>
					</Box>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 2
						}}
					>
						Professionals agree to indemnify and hold Nami and its affiliates harmless from third-party claims or regulatory actions arising from a Professional's use of the Texting Services.
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 3
						}}
					>
						Nami may restrict or terminate access to Texting Services at any time.
					</Typography>

					<Typography
						variant="h3"
						component="h3"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '16px', md: '18px' },
							color: '#2D3748',
							mb: 1.5,
							mt: 3
						}}
					>
						9.2 For Customers
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 2
						}}
					>
						If you consent to receive Text Messages from a Professional, you may receive administrative and/or marketing messages related to pet care services you receive from that Professional. Consent to receive automated Text Messages is not a condition of booking an appointment with, or receiving services from, a Professional.
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 2
						}}
					>
						<strong>Opt-out:</strong> Reply STOP to unsubscribe from a Professional's Text Messages. You will receive one additional message confirming your request has been processed. Opting out from one Professional or one service may not opt you out of other messages unless you unsubscribe separately.
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 2
						}}
					>
						<strong>Help:</strong> For help, contact the Professional you originally gave consent to, or contact Nami at admin@nami.pet.
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 2
						}}
					>
						<strong>Carriers:</strong> Message and data rates may apply. Mobile carriers and Nami are not responsible for delayed or undelivered messages.
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 3
						}}
					>
						<strong>Privacy:</strong> See the Nami Privacy Policy for information about personal information processed in connection with Texting Services.
					</Typography>

					{/* Section 10 */}
					<Typography
						variant="h2"
						component="h2"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '18px', md: '22px' },
							color: '#2D3748',
							mb: 2,
							mt: 4
						}}
					>
						10) Disclaimers
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 3
						}}
					>
						The Services are provided "as is" and "as available." We do not guarantee that the Services will be uninterrupted or error-free.
					</Typography>

					{/* Section 11 */}
					<Typography
						variant="h2"
						component="h2"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '18px', md: '22px' },
							color: '#2D3748',
							mb: 2,
							mt: 4
						}}
					>
						11) Indemnity
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 3
						}}
					>
						To the extent permitted by applicable law, you agree to indemnify and defend Nami and its affiliates and personnel for third-party claims arising from your User Content, your use of the Services, or your violation of these Terms.
					</Typography>

					{/* Section 12 */}
					<Typography
						variant="h2"
						component="h2"
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: { xs: '18px', md: '22px' },
							color: '#2D3748',
							mb: 2,
							mt: 4
						}}
					>
						12) General
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 2
						}}
					>
						These Terms do not create a partnership, joint venture, employment, or agency relationship between you and Nami.
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568',
							mb: 2
						}}
					>
						If any provision is unenforceable, the remaining provisions will remain in effect.
					</Typography>

					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: { xs: '14px', md: '16px' },
							lineHeight: 1.7,
							color: '#4A5568'
						}}
					>
						Contact: admin@nami.pet
					</Typography>
				</Box>
			</Container>
		</Box>
	)
}
